import { MihatoMark } from "@/common/components/MihatoMark";
import { ApiError } from "@/common/lib/api";
import {
  clearCurrentAccountId,
  writeCurrentAccountId,
} from "@/features/accounts/hooks/useCurrentAccount";
import { useBootstrap } from "@/features/auth/api/authApi";
import { completeEmailLinkSignIn, isAuthLink } from "@/features/auth/lib/firebaseSignIn";
import {
  clearPendingEmail,
  clearPendingInvitationToken,
  readPendingEmail,
  readPendingInvitationToken,
} from "@/features/auth/lib/storage";
import { validateEmailOnly } from "@/features/auth/schemas";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

type State =
  | { kind: "verifying" }
  | { kind: "needs-email" }
  | { kind: "bootstrapping" }
  | { kind: "success" }
  | { kind: "error"; title: string; message: string; showLoginLink?: boolean };

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "verifying" });
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const bootstrap = useBootstrap();
  // StrictMode dispara useEffect dos veces en dev y la segunda corrida fallaría
  // porque el action code ya fue consumido.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      try {
        if (!isAuthLink(window.location.href)) {
          setState({
            kind: "error",
            title: "Link inválido o expirado",
            message:
              "Este link no es válido o ya fue usado. Pedí uno nuevo desde la página de ingreso.",
            showLoginLink: true,
          });
          return;
        }

        const storedEmail = readPendingEmail();
        if (!storedEmail) {
          setState({ kind: "needs-email" });
          return;
        }

        await runSignInAndBootstrap(storedEmail);
      } catch (err) {
        setState(mapError(err));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runSignInAndBootstrap(email: string): Promise<void> {
    setState({ kind: "verifying" });
    await completeEmailLinkSignIn(email, window.location.href);
    clearPendingEmail();
    clearCurrentAccountId();

    setState({ kind: "bootstrapping" });
    const invitationToken = readPendingInvitationToken() ?? undefined;
    try {
      const result = await bootstrap.mutateAsync({ invitationToken });
      clearPendingInvitationToken();
      setState({ kind: "success" });

      // Si venimos por invitación, fijamos esa cuenta como activa.
      // El resto del routing (0 memberships → /onboarding/create-account,
      // ≥1 → /app/hato) lo decide AccountProvider en /app a partir de /me.
      // Una sola fuente de verdad: el cache de useMe.
      if (result.acceptedInvitation?.accountId) {
        writeCurrentAccountId(result.acceptedInvitation.accountId);
      }
      navigate("/app", { replace: true });
    } catch (err) {
      clearPendingInvitationToken();
      setState(mapBootstrapError(err));
    }
  }

  async function handleEmailSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const validationError = validateEmailOnly(emailInput);
    if (validationError) {
      setEmailError(validationError);
      return;
    }
    setEmailError(null);
    try {
      await runSignInAndBootstrap(emailInput.trim().toLowerCase());
    } catch (err) {
      setState(mapError(err));
    }
  }

  return (
    <>
      <title>Verificando acceso · HATO</title>
      <meta name="robots" content="noindex, nofollow" />

      <main className="relative flex min-h-screen flex-col items-center justify-center bg-brand-dark px-6 py-12 text-center text-white">
        <Link to="/" className="mb-10 flex items-center gap-3">
          <MihatoMark className="h-7 w-7 text-white" />
          <span className="font-medium text-lg tracking-tight">HATO</span>
        </Link>

        {(state.kind === "verifying" || state.kind === "bootstrapping") && (
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/[0.1] border-t-primary-500" />
            <p className="text-neutral-400 text-sm">
              {state.kind === "verifying" ? "Verificando tu acceso…" : "Completando el ingreso…"}
            </p>
          </div>
        )}

        {state.kind === "needs-email" && (
          <form
            onSubmit={handleEmailSubmit}
            noValidate
            className="flex w-full max-w-md flex-col gap-4 rounded-2xl border border-white/[0.06] bg-brand-panel p-6 text-left"
          >
            <div className="flex flex-col gap-1">
              <h1 className="font-medium text-2xl text-white tracking-tight">Reingresá tu email</h1>
              <p className="text-neutral-400 text-sm leading-relaxed">
                Por seguridad, necesitamos que confirmes el email al que enviamos el link.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="callback-email" className="font-medium text-neutral-300 text-sm">
                Correo
              </label>
              <input
                id="callback-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                placeholder="tu@ejemplo.com"
                aria-invalid={Boolean(emailError)}
                className="h-12 w-full rounded-xl border border-white/[0.08] bg-brand-dark px-4 text-base text-white placeholder:text-neutral-500 focus:border-primary-500/60 focus:outline-none focus:ring-1 focus:ring-primary-500/40"
              />
              {emailError && (
                <p role="alert" className="text-rose-400 text-xs">
                  {emailError}
                </p>
              )}
            </div>
            <button
              type="submit"
              className="mt-2 flex h-12 items-center justify-center rounded-full bg-primary-500 px-6 font-medium text-brand-dark text-sm transition-colors hover:bg-primary-400"
            >
              Continuar
            </button>
          </form>
        )}

        {state.kind === "error" && (
          <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border border-rose-500/20 bg-rose-500/[0.04] p-6">
            <h1 className="font-medium text-2xl text-white tracking-tight">{state.title}</h1>
            <p className="text-neutral-300 text-sm leading-relaxed">{state.message}</p>
            <div className="flex flex-col gap-2 self-stretch">
              {state.showLoginLink && (
                <Link
                  to="/login"
                  className="flex h-12 items-center justify-center rounded-full bg-primary-500 px-6 font-medium text-brand-dark text-sm transition-colors hover:bg-primary-400"
                >
                  Volver a ingresar
                </Link>
              )}
              <Link
                to="/access-request"
                className="flex h-12 items-center justify-center rounded-full border border-white/[0.1] px-6 font-medium text-sm text-white transition-colors hover:bg-white/[0.05]"
              >
                Solicitar acceso
              </Link>
            </div>
          </div>
        )}
      </main>
    </>
  );
}

function mapError(err: unknown): State {
  const msg = err instanceof Error ? err.message : String(err);
  const code =
    err && typeof err === "object" && "code" in err
      ? String((err as { code?: string }).code ?? "")
      : "";

  if (
    code === "auth/invalid-action-code" ||
    code === "auth/expired-action-code" ||
    msg.includes("invalid-action-code") ||
    msg.includes("expired-action-code")
  ) {
    return {
      kind: "error",
      title: "Link inválido o expirado",
      message: "Este link ya fue usado o expiró. Pedí uno nuevo desde la página de ingreso.",
      showLoginLink: true,
    };
  }

  if (code === "auth/invalid-email") {
    return {
      kind: "error",
      title: "Email inválido",
      message: "El email que ingresaste no es válido. Volvé a /login y probá de nuevo.",
      showLoginLink: true,
    };
  }

  return {
    kind: "error",
    title: "No pudimos completar el ingreso",
    message: "Hubo un problema verificando tu acceso. Volvé a intentar desde la página de ingreso.",
    showLoginLink: true,
  };
}

function mapBootstrapError(err: unknown): State {
  if (err instanceof ApiError) {
    if (err.status === 403 && err.code === "NO_ACCESS") {
      return {
        kind: "error",
        title: "No tenés acceso al sistema",
        message:
          "¿Venís de una invitación? Abrí el link en el mismo navegador donde hiciste click originalmente. Si no, podés solicitar acceso.",
        showLoginLink: false,
      };
    }
    if (err.status === 403 && err.code === "INVITATION_INVALID") {
      return {
        kind: "error",
        title: "Invitación inválida",
        message: "La invitación expiró o fue cancelada. Pedile al admin que te envíe una nueva.",
        showLoginLink: true,
      };
    }
    if (err.status === 403 && err.code === "EMAIL_MISMATCH") {
      return {
        kind: "error",
        title: "El email no coincide",
        message:
          "El email de tu cuenta no coincide con el de la invitación. Entrá con el email al que te invitaron.",
        showLoginLink: true,
      };
    }
    if (err.status === 400 && err.code === "MISSING_EMAIL") {
      return {
        kind: "error",
        title: "No pudimos verificar tu email",
        message: "Volvé a /login y pedí un nuevo link.",
        showLoginLink: true,
      };
    }
    if (err.status === 401) {
      return {
        kind: "error",
        title: "Tu sesión expiró",
        message: "Volvé a intentar desde la página de ingreso.",
        showLoginLink: true,
      };
    }
  }
  return {
    kind: "error",
    title: "No pudimos completar el ingreso",
    message: "Hubo un problema con el servidor. Volvé a intentar en un momento.",
    showLoginLink: true,
  };
}
